<script setup>

import BaseModal from "@/components/global-themed/BaseModal.vue";
import ContentWizardText from "@/components/actions/partials/settings/ContentWizardText.vue";
import ContentWizardStepTwo from "@/components/actions/partials/settings/ContentWizardStepTwo.vue";
import {useMutation, useQuery, useQueryClient} from "@tanstack/vue-query";
import axios from "axios";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import {errorToast} from "@/composables/useToasts";

const emit = defineEmits(['close']);

const props = defineProps({
  page: {
    required: true,
    type: Object,
  },
});

const {data, isLoading} = useQuery({
  queryKey: ['content-wizard-index', props.page.id],
  queryFn: async () => {
    const response = await axios.get(`/ajax/v2/actions/page/${props.page.id}/content-wizard`);

    return response.data.data;
  },
});

const queryClient = useQueryClient();

const {mutate: runContentWizard, isPending} = useMutation({
  mutationFn: pageId => axios.post(`/ajax/v2/actions/page/${pageId}/content-wizard`),
  onSuccess: async ({data}, pageId) => {
    await queryClient.cancelQueries({queryKey: ['content-wizard-index']});

    const previousData = queryClient.getQueryData(['content-wizard-index', pageId]);

    queryClient.setQueryData(['content-wizard-index', pageId], (old) => ({
      ...old,
      credits: data.data.credits,
      content: data.data.content,
    }));

    return {previousData}
  },
  onError: (error) => {
    const {message} = error.response.data;
    errorToast(message ?? 'Something went wrong generating content');
  },
  onSettled: () => {
    queryClient.invalidateQueries({queryKey: ['content-wizard-index']});
  },
});
</script>

<template>
  <Teleport
    to=".tailwind"
  >
    <BaseModal
      @close="emit('close')"
    >
      <LoadingBlock v-if="isLoading" />

      <ContentWizardText
        v-else-if="data && !data.content"
        :data="data"
        :loading="isPending"
        :is-loading="isLoading"
        @click="runContentWizard(page.id)"
      />

      <ContentWizardStepTwo
        v-else
        :data="data"
        :page-id="page.id"
        :loading="isPending"
        @run-content-wizard="runContentWizard(page.id)"
      />
    </BaseModal>
  </Teleport>
</template>
